import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { CloseOutlined, CarOutlined } from '@ant-design/icons';
import carIcon from '../../../signal_fix/assets/icons/car_icon.png';
import { FaPause, FaPlay } from 'react-icons/fa';

/**
 * SimulazioneModal - Green Wave Simulation Modal
 * Shows a visualization of traffic lights and vehicle movement
 *
 * 1D View: Traffic lights in a row with animated car
 * 2D View: Distance vs Time chart with rectangles (static display, no animation)
 */
function SimulazioneModal({
  visible,
  onClose,
  simulationData,
  velocity = 50, // km/h
  onTrafficLightStateChange, // Callback to sync map markers with animation
}) {
  const [viewMode, setViewMode] = useState('1D'); // '1D' or '2D'
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0); // seconds
  const [speedMultiplier, setSpeedMultiplier] = useState(1); // 0.25x to 3x
  const animationRef = useRef(null);
  const isPlayingRef = useRef(false);
  const lastFrameTimeRef = useRef(0);

  // Drag state for movable modal
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const dragStartRef = useRef({ x: 0, y: 0 });
  const modalRef = useRef(null);

  // Sync ref with state
  useEffect(() => {
    isPlayingRef.current = isPlaying;
  }, [isPlaying]);

  // Extract data from simulationData (from API response)
  const semafori = useMemo(() => {
    if (!simulationData) return [];

    let rawList = [];
    let requestParams = simulationData.requestParams;

    // Handle new API format
    const resultType = simulationData.result?.type;
    const finalResult = resultType === 'global' ? simulationData.result?.global_result : simulationData.result?.corridor_result;

    if (finalResult) {
      const offsets = finalResult.offsets || {};
      const corridor = simulationData.signals?.map(s => s.id) || requestParams?.signals?.map(s => s.id) || [];
      const inputSemafori = requestParams?.signals || simulationData.signals || [];
      const linksArray = requestParams?.links || simulationData.links || [];

      rawList = corridor.map((id, index) => {
        const input = inputSemafori.find(s => s.id === id);
        const durata_verde = input?.green_duration || input?.durata_verde || 20;

        let linkDistance = 0;
        let crossingLength = 0;
        let linkSpeed = velocity;
        
        if (index > 0) {
          const prevId = corridor[index - 1];
          const linkObj = linksArray.find(l => l.from_signal === prevId && l.to_signal === id);
          if (linkObj) {
            linkDistance = linkObj.distance_m;
            if (linkObj.speed_kmh) linkSpeed = linkObj.speed_kmh;
          }
          
          // Get crossing distance of the previous traffic light
          const prevInput = inputSemafori.find(s => s.id === prevId);
          crossingLength = prevInput?.crossing_length_m !== undefined ? prevInput.crossing_length_m : (prevInput?.lunghezza_attraversamento !== undefined ? prevInput.lunghezza_attraversamento : 10);
        }

        return {
          id: id,
          semaforo: input?.nome || id,
          distanza: input?.distanza !== undefined ? input.distanza : (linkDistance + crossingLength),
          crossing_length_m: crossingLength,
          velocita_link: linkSpeed,
          durata_verde: durata_verde,
          green_start: input?.green_start || 0,
          yellow_time: input?.yellow_time || 3,
          red_time: input?.red_time || 30,
          inizio_verde: offsets[id] || 0,
          fine_verde: (offsets[id] || 0) + (input?.green_start || 0) + durata_verde,
        };
      });
    } else if (simulationData.final_result) {
      // Handle older format
      const { offsets, corridor } = simulationData.final_result;
      const inputSemafori = requestParams?.semafori || [];

      rawList = corridor.map(id => {
        const input = inputSemafori.find(s => s.id === id);
        return {
          id: id,
          semaforo: input?.nome || id,
          distanza: input?.distanza || 0,
          velocita_link: input?.velocita || velocity,
          crossing_length_m: input?.lunghezza_attraversamento || input?.crossing_length_m || 10,
          durata_verde: input?.durata_verde || 20,
          green_start: input?.green_start || 0,
          yellow_time: input?.yellow_time || 3,
          red_time: input?.red_time || 30,
          inizio_verde: offsets[id] || 0,
          fine_verde: (offsets[id] || 0) + (input?.green_start || 0) + (input?.durata_verde || 20),
        };
      });
    } else {
      // Fallback for very old API format
      rawList = (simulationData?.risultati || []).map(s => ({
        ...s,
        id: s.id || s.semaforo,
        inizio_verde: s.inizio_verde || 0,
        fine_verde: s.fine_verde || (s.tempo || 30),
        velocita_link: s.velocita || velocity,
        crossing_length_m: s.lunghezza_attraversamento || s.crossing_length_m || 10,
        durata_verde: s.durata_verde || s.tempo || 30,
        green_start: s.green_start || 0,
        yellow_time: s.yellow_time || 3,
        red_time: s.red_time || 30,
      }));
    }

    // Calculate cumulative distances
    let cumulativeDistance = 0;
    return rawList.map((s, index) => {
      cumulativeDistance += (s.distanza || 0);
      return {
        ...s,
        cumulativeDistance,
        index
      };
    });
  }, [simulationData]);

  // Calculate total distance (cumulative)
  const totalDistance = semafori.length > 0
    ? semafori[semafori.length - 1].cumulativeDistance
    : 100;

  // First signal distance (for car starting position)
  const firstSignalDistance = semafori.length > 0 ? semafori[0].cumulativeDistance : 0;

  // Car starts 20m before first traffic light
  const carStartOffset = 20;

  // Calculate velocity in m/s
  const velocityMs = (velocity * 1000) / 3600; // m/s

  // Helper: Get cycle time for a signal
  const getCycleTime = (s) => (s.durata_verde || 20) + (s.yellow_time || 3) + (s.red_time || 30);

  // Get traffic light state based on current time (cyclic)
  const getTrafficLightState = useCallback((s, t) => {
    if (!s) return 'red';
    const T = getCycleTime(s);
    const offset = s.inizio_verde || 0;
    const green_start = s.green_start || 0;
    
    // Normalized time within cycle [0, T)
    const tCycle = ((t - offset) % T + T) % T;

    if (tCycle >= green_start && tCycle < green_start + (s.durata_verde || 20)) return 'green';
    if (tCycle >= green_start + (s.durata_verde || 20) && tCycle < green_start + (s.durata_verde || 20) + (s.yellow_time || 3)) return 'yellow';
    return 'red';
  }, []);



  // Calculate total simulation duration dynamically
  const totalDuration = useMemo(() => {
    let simTime = 0;
    let simPos = firstSignalDistance - carStartOffset;
    
    if (simulationData?.final_result?.intervals && simulationData.final_result.intervals.length > 0) {
      const firstValidStart = simulationData.final_result.intervals[0][0];
      let runupTime = 0;
      if (carStartOffset > 0) {
        const initialLinkSpeed = (semafori[0].velocita_link || velocity) * 1000 / 3600;
        runupTime = carStartOffset / initialLinkSpeed;
      }
      simTime = Math.max(0, firstValidStart - runupTime);
    }

    // Simulate same path run-up to get exact sim time
    if (carStartOffset > 0 && semafori.length > 0) {
      const initialLinkSpeed = (semafori[0].velocita_link || velocity) * 1000 / 3600;
      simTime += (carStartOffset / initialLinkSpeed);
      simPos += carStartOffset;
    }

    for (let i = 0; i < semafori.length; i++) {
      const s = semafori[i];
      const crossing_m = s.crossing_length_m || 10;
      const stoppingPos = s.cumulativeDistance - crossing_m;
      
      if (stoppingPos > simPos) {
        const distToSignal = stoppingPos - simPos;
        const currentVelocityMs = (s.velocita_link || velocity) * 1000 / 3600;
        const timeToReach = distToSignal / currentVelocityMs;
        
        simTime += timeToReach;
        simPos = stoppingPos;
      }

      // Check if car needs to wait
      const state = getTrafficLightState(s, simTime);
      if (state !== 'green') {
        const T = getCycleTime(s);
        const offset = s.inizio_verde || 0;
        const green_start = s.green_start || 0;
        
        const tCycle = ((simTime - offset) % T + T) % T;
        let timeToNextGreen = 0;
        if (tCycle < green_start) {
          timeToNextGreen = green_start - tCycle;
        } else {
          timeToNextGreen = T - tCycle + green_start;
        }
        simTime += timeToNextGreen;
      }
      
      const currentVelocityMs = (s.velocita_link || velocity) * 1000 / 3600;
      const crossingVelocityMs = currentVelocityMs * 0.7; // Factor 0.7 for intersection crossing
      const crossingTime = crossing_m / crossingVelocityMs;
      
      simTime += crossingTime;
      simPos += crossing_m;
    }

    // Ensure duration covers at least the first green wave interval
    let minDuration = simTime + 60;
    if (simulationData?.final_result?.intervals && simulationData.final_result.intervals.length > 0) {
      const firstIntervalEnd = simulationData.final_result.intervals[0][1];
      minDuration = Math.max(minDuration, firstIntervalEnd + 60);
    }

    return minDuration;
  }, [semafori, velocity, firstSignalDistance, carStartOffset, getTrafficLightState, simulationData]);

  // --- CAR FOLLOWING MODEL (CFM) PARAMETERS ---
  const DT = 0.1; // seconds per frame
  const GAP_MIN = 10; // meters (minimum gap between cars)
  // Dynamic max acceleration and deceleration limits based on current speed targets
  // Allows cars to actually reach their targeted speed quickly in the simulation
  // instead of taking too long to speed up or slow down
  const A_MAX = 50.0; // m/s^2 (Aggressive acceleration for near-instant physics matching)
  const B_MAX = 50.0; // m/s^2 (Aggressive deceleration for near-instant physics matching)

  const precomputedData = useMemo(() => {
    if (semafori.length === 0 || !totalDuration) return null;

    const numFrames = Math.ceil(totalDuration / DT) + 1;
    const s0 = semafori[0];
    const T = getCycleTime(s0);
    const offset = s0.inizio_verde || 0;
    
    // Find valid green intervals for S1
    let baseIntervals = [];
    if (simulationData?.final_result?.intervals && simulationData.final_result.intervals.length > 0) {
      baseIntervals = simulationData.final_result.intervals;
    } else {
      const green_start = s0.green_start || 0;
      baseIntervals = [[offset + green_start, offset + green_start + (s0.durata_verde || 20)]];
    }

    const spawnTimes = [];
    // Iterate through cycles to generate cars
    for (let k = 0; k * T < totalDuration; k++) {
      // Create a max of 3 cars per cycle per interval
      for (const [intStart, intEnd] of baseIntervals) {
        const startGreen = intStart + k * T;
        const endGreen = intEnd + k * T;
        
        // Only generate vehicles if the valid interval has a duration
        if (endGreen > startGreen) {
          // Calculate runup time needed to reach the stop line exactly at the target time
          let runupTime = 0;
          if (carStartOffset > 0) {
            const initialLinkSpeed = (s0.velocita_link || velocity) * 1000 / 3600;
            runupTime = carStartOffset / initialLinkSpeed;
          }

          // Generate 3 cars per valid green wave interval (Start, Middle, End)
          const t1 = Math.max(0, startGreen - runupTime);
          const t2 = Math.max(0, startGreen + (endGreen - startGreen) / 2 - runupTime);
          const t3 = Math.max(0, endGreen - runupTime);

          if (startGreen < totalDuration) spawnTimes.push(t1);
          if (startGreen + (endGreen - startGreen) / 2 < totalDuration) spawnTimes.push(t2);
          if (endGreen < totalDuration) spawnTimes.push(t3);
        }
      }
    }

    const numCars = spawnTimes.length;
    // Initialize cars
    const cars = spawnTimes.map((spawnTime, i) => ({
      id: i,
      spawnTime: spawnTime,
      active: false,
      pos: firstSignalDistance - carStartOffset,
      v: velocityMs, // Start perfectly at cruising speed (minus acceleration delay)
      trajectory: new Float32Array(numFrames),
      isWaiting: new Uint8Array(numFrames),
    }));

    // Pre-calculate stop lines (stop line is purely link distance, crossing length follows it)
    // S2 Cumulative distance already includes crossing S1
    // S2 pos = (S1 cum) + dist_link
    const stopLines = semafori.map((s, idx) => {
      const prevCumDist = idx > 0 ? semafori[idx - 1].cumulativeDistance : 0;
      // Subtracting crossing length to find the actual stop line
      const crossing_m = s.crossing_length_m || 10;
      return {
        ...s,
        pos: s.cumulativeDistance - crossing_m, // The stop line before intersection
      };
    });

    for (let f = 0; f < numFrames; f++) {
      const t = f * DT;

      // Determine traffic light states at time t
      const lightStates = stopLines.map(s => getTrafficLightState(s, t));

      for (let i = 0; i < numCars; i++) {
        const car = cars[i];

        if (t < car.spawnTime) {
          car.trajectory[f] = car.pos;
          car.isWaiting[f] = 0;
          continue;
        }

        if (!car.active) {
          car.active = true;
          // When spawning, ensure it doesn't overlap with the car ahead
          if (i > 0) {
            const leader = cars[i - 1];
            if (leader.pos - car.pos < GAP_MIN) {
              car.pos = leader.pos - GAP_MIN;
              car.v = 0; // Start at rest if queued immediately
            }
          }
        }

        // Find the closest obstacle
        let d_obs = Infinity;

        // 1. Leader car
        if (i > 0) {
          const leader = cars[i - 1];
          if (leader.pos > car.pos) {
            d_obs = Math.min(d_obs, leader.pos - car.pos - GAP_MIN); // free space to leader
          } else {
            d_obs = -1;
          }
        }

        // 2. Next Red/Yellow Light
        for (let j = 0; j < stopLines.length; j++) {
          const s = stopLines[j];
          if (s.pos - car.pos > -1 && lightStates[j] !== 'green') {
            d_obs = Math.min(d_obs, s.pos - car.pos);
            break; // Only the immediate next light matters
          }
        }

        // Find the current link speed and apply intersection crossing logic
        let currentLinkVelocityMs = velocityMs;
        let isCrossing = false;
        
        for (let j = 0; j < stopLines.length; j++) {
          if (car.pos < stopLines[j].pos) {
            currentLinkVelocityMs = (stopLines[j].velocita_link || velocity) * 1000 / 3600;
            break;
          } else if (car.pos >= stopLines[j].pos && car.pos <= stopLines[j].cumulativeDistance) {
             // Car is currently traversing an intersection (crossing length)
             // Speeds in intersections are reduced
             currentLinkVelocityMs = ((stopLines[j].velocita_link || velocity) * 1000 / 3600) * 0.7; // Factor of 0.7 as per logic
             isCrossing = true;
             break;
          } else if (j === stopLines.length - 1 && car.pos > stopLines[j].cumulativeDistance) {
             // Past last traffic light
             currentLinkVelocityMs = (stopLines[j].velocita_link || velocity) * 1000 / 3600;
          }
        }
        
        const targetSpeed = currentLinkVelocityMs;

        // Calculate target velocity based on obstacle distance
        let v_safe = targetSpeed;
        if (d_obs <= 0) {
          v_safe = 0;
        } else if (d_obs !== Infinity && !isCrossing) { // Don't brake for intersection distance if already crossing
          // Kinematic safe speed: v = sqrt(2 * b_max * d)
          v_safe = Math.sqrt(2 * B_MAX * d_obs);
          v_safe = Math.min(targetSpeed, v_safe);
        } else if (isCrossing) {
          // If crossing an intersection, override safe speed down to crossing speed target
          v_safe = Math.min(v_safe, targetSpeed);
        }

        // Apply acceleration/deceleration constraints
        // Using explicit physics equations with constraints
        if (v_safe > car.v) {
          car.v = Math.min(car.v + A_MAX * DT, v_safe); // Accelerating to safe speed
        } else {
          car.v = Math.max(car.v - B_MAX * DT, v_safe); // Decelerating to safe speed
        }

        // Prevent reversing and enforce strict target speed limits
        car.v = Math.max(0, Math.min(car.v, targetSpeed));

        // Update position
        car.pos += car.v * DT;

        // Save state for this frame
        car.trajectory[f] = car.pos;
        car.isWaiting[f] = car.v < 0.1 ? 1 : 0;
      }
    }

    return { cars, dt: DT };
  }, [semafori, velocityMs, velocity, firstSignalDistance, carStartOffset, getTrafficLightState, totalDuration]);

  // Animation loop using real time delta and speed multiplier
  const animate = useCallback((timestamp) => {
    if (!lastFrameTimeRef.current) {
      lastFrameTimeRef.current = timestamp;
    }
    // Prevent negative deltas or massive jumps
    let deltaSeconds = (timestamp - lastFrameTimeRef.current) / 1000;
    if (deltaSeconds < 0 || deltaSeconds > 1) {
      deltaSeconds = 0;
    }
    
    lastFrameTimeRef.current = timestamp;

    setCurrentTime(prevTime => {
      const nextTime = prevTime + (deltaSeconds * speedMultiplier);
      if (nextTime >= totalDuration) {
        setIsPlaying(false);
        isPlayingRef.current = false;
        return totalDuration;
      }
      return nextTime;
    });

    if (isPlayingRef.current) {
      animationRef.current = requestAnimationFrame(animate);
    }
  }, [speedMultiplier, totalDuration]);

  // Start/stop animation
  useEffect(() => {
    if (isPlaying) {
      isPlayingRef.current = true;
      // Clear the ref so animate initializes it with the very first rAF timestamp
      lastFrameTimeRef.current = 0; 
      animationRef.current = requestAnimationFrame(animate);
    } else {
      isPlayingRef.current = false;
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    }

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [isPlaying, animate]);

  // Reset animation and position when modal closes or opens
  useEffect(() => {
    if (!visible) {
      setIsPlaying(false);
      setCurrentTime(0);
      setPosition({ x: 0, y: 0 });
      if (onTrafficLightStateChange) {
        onTrafficLightStateChange(null);
      }
    }
  }, [visible, onTrafficLightStateChange]);

  // Notify parent of traffic light state changes during animation
  useEffect(() => {
    if (!isPlaying || !onTrafficLightStateChange || semafori.length === 0) {
      return;
    }

    const stateMap = {};
    semafori.forEach((semaforo) => {
      const state = getTrafficLightState(semaforo, currentTime);
      const lightId = semaforo.semaforo || semaforo.id || semaforo.nome;
      if (lightId) {
        stateMap[lightId] = state;
      }
    });

    onTrafficLightStateChange(stateMap);
  }, [currentTime, isPlaying, semafori, onTrafficLightStateChange, getTrafficLightState]);

  // Drag handlers for movable modal
  const handleDragStart = useCallback((e) => {
    if (e.target.closest('button') || e.target.closest('input')) return;
    setIsDragging(true);
    dragStartRef.current = {
      x: e.clientX - position.x,
      y: e.clientY - position.y
    };
  }, [position]);

  const handleDragMove = useCallback((e) => {
    if (!isDragging) return;
    setPosition({
      x: e.clientX - dragStartRef.current.x,
      y: e.clientY - dragStartRef.current.y
    });
  }, [isDragging]);

  const handleDragEnd = useCallback(() => {
    setIsDragging(false);
  }, []);

  // Add global mouse event listeners for drag
  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleDragMove);
      document.addEventListener('mouseup', handleDragEnd);
      return () => {
        document.removeEventListener('mousemove', handleDragMove);
        document.removeEventListener('mouseup', handleDragEnd);
      };
    }
  }, [isDragging, handleDragMove, handleDragEnd]);

  const handlePlayPause = () => {
    if (!isPlaying && currentTime >= totalDuration) {
      setCurrentTime(0);
    }
    setIsPlaying(!isPlaying);
  };

  // Calculate cumulative distance for 2D chart (stop line to stop line)
  const dist2D = useMemo(() => {
    let dist = [0.0];
    for (let i = 0; i < semafori.length; i++) {
      if (i > 0) {
        // Find distance to current signal
        const prevId = semafori[i - 1].id;
        const currId = semafori[i].id;
        let d = 0;
        // Use raw links array to get exact distance (semafori distances are already cumulative)
        const linksArray = simulationData?.requestParams?.links || simulationData?.links || [];
        const linkObj = linksArray.find(l => l.from_signal === prevId && l.to_signal === currId);
        if (linkObj) {
           d = linkObj.distance_m;
        } else {
           d = semafori[i].distanza || 0; // fallback if links array not found
        }
        dist.push(dist[dist.length - 1] + d);
      }
    }
    return dist;
  }, [semafori, simulationData]);

  // Chart dimensions and scaling
  const chartHeight = 700;
  const chartWidth = 1400; // Use a high-res base and let CSS scale it

  // Calculate max values for scaling - add extra padding to Y axis
  const rawMaxDistance = Math.ceil(dist2D[dist2D.length - 1] / 50) * 50 || 100;
  const minY = -100; // Extend domain below zero so S1 is lifted off the bottom border
  const maxY = rawMaxDistance + 350; // Add padding top
  const maxDistance = maxY; // Ensure maxDistance mapping stays correct
  const yRange = maxY - minY;
  const maxTime = 600; // Force horizon to 600s exactly like Python

  // Scaling factors
  const xScale = chartWidth / maxTime; // Scale to fit width
  const yScale = chartHeight / yRange; // pixels per meter

  // For chart padding/alignment, define inner bounds
  const paddingLeft = 0;
  const paddingBottom = 0;
  const innerWidth = chartWidth - paddingLeft;
  const innerHeight = chartHeight - paddingBottom;
  
  // Re-adjust scaling to fit strictly within the inner bounding box
  const xScaleInner = innerWidth / maxTime;
  const yScaleInner = innerHeight / maxDistance;

  // Generate scale marks for X axis (every 50m with 5px padding)
  const { scaleMarks, maxScaleDistance } = useMemo(() => {
    const marks = [];
    const scaleStep = 150;
    for (let i = 0; i <= totalDistance + carStartOffset; i += scaleStep) {
      marks.push(i);
    }
    if (marks.length === 0 || marks[marks.length - 1] < totalDistance) {
      marks.push(Math.ceil(totalDistance / scaleStep) * scaleStep);
    }
    const maxDist = marks[marks.length - 1] || totalDistance || 100;
    return { scaleMarks: marks, maxScaleDistance: maxDist };
  }, [totalDistance, carStartOffset]);

  // Calculate cumulative real travel times (cum_tt) for 2D chart
  const cum_tt = useMemo(() => {
    let tt = [0.0];
    for (let i = 0; i < semafori.length - 1; i++) {
      const s_to = semafori[i + 1];
      
      // travel_time_link
      const prevId = semafori[i].id;
      const currId = semafori[i + 1].id;
      const linksArray = simulationData?.requestParams?.links || simulationData?.links || [];
      const linkObj = linksArray.find(l => l.from_signal === prevId && l.to_signal === currId);
      
      let distance_m = 0;
      if (linkObj) distance_m = linkObj.distance_m;
      else distance_m = semafori[i + 1].distanza || 0;
      
      const v_link = (linkObj?.speed_kmh || velocity) * 1000 / 3600;
      const t_link = distance_m / v_link;
      
      // crossing_time
      const v_cross = v_link * 0.7; // crossing_speed_factor
      const crossing_length_m = s_to.crossing_length_m || 10;
      const t_cross = crossing_length_m / v_cross;
      
      // travel_time_between_signals
      tt.push(tt[tt.length - 1] + t_link + t_cross);
    }
    return tt;
  }, [semafori, velocity, simulationData]);

  // compute_effective_green (returns RELATIVE to start of cycle)
  const getEffectiveGreenRelative = useCallback((s, hasLinkIn) => {
    const startup_lost_time = 2.0;
    const clearance_time = 2.0;
    const green_start = s.green_start || 0;
    
    let g_start = green_start + startup_lost_time;
    let g_end = green_start + (s.durata_verde || 20) - clearance_time;
    
    if (hasLinkIn && (s.crossing_length_m || 10) > 0) {
      const sIndex = semafori.findIndex(sem => sem.id === s.id);
      let v_link = velocity * 1000 / 3600;
      if (sIndex > 0) {
          const prevId = semafori[sIndex - 1].id;
          const linksArray = simulationData?.requestParams?.links || simulationData?.links || [];
          const linkObj = linksArray.find(l => l.from_signal === prevId && l.to_signal === s.id);
          if (linkObj && linkObj.speed_kmh) v_link = linkObj.speed_kmh * 1000 / 3600;
      }
      
      const v_cross = v_link * 0.7;
      const t_cross = (s.crossing_length_m || 10) / v_cross;
      g_end -= t_cross;
    }
    
    if (g_end < g_start) g_end = g_start;
    
    return [g_start, g_end];
  }, [semafori, velocity, simulationData]);

  // Y axis ticks for 2D view
  const yAxisTicks = useMemo(() => {
    const ticks = [];
    const step = 500;
    for (let i = 0; i <= maxY; i += step) {
      ticks.push(i);
    }
    return ticks;
  }, [maxY]);

  // Calculate actual trajectory for 2D chart based strictly on physics parameters
  const actualTrajectoryPoints = useMemo(() => {
    if (semafori.length === 0) return [];

      let points = [];
      let simPos = firstSignalDistance; // Start directly at S1, no run-up line
      let simTime = 0;

      if (simulationData?.final_result?.intervals && simulationData.final_result.intervals.length > 0) {
        simTime = simulationData.final_result.intervals[0][0]; // Start moving precisely at the green wave!
      } else {
        // Fallback: wait until S1 is green
        const s0 = semafori[0];
        const state = getTrafficLightState(s0, simTime);
        if (state !== 'green') {
          const T = getCycleTime(s0);
          const offset = s0.inizio_verde || 0;
          const green_start = s0.green_start || 0;
          
          const tCycle = ((simTime - offset) % T + T) % T;
          if (tCycle < green_start) simTime += (green_start - tCycle);
          else simTime += (T - tCycle + green_start);
        }
      }

      // Starting point
      points.push({ t: simTime, x: simPos });

      // First runup from starting offset to first signal
      if (carStartOffset > 0 && semafori.length > 0) {
        const initialLinkSpeed = (semafori[0].velocita_link || velocity) * 1000 / 3600;
        const timeToFirstSignal = carStartOffset / initialLinkSpeed;
        simTime += timeToFirstSignal;
        simPos += carStartOffset;
        points.push({ t: simTime, x: simPos });
      }

      for (let i = 0; i < semafori.length; i++) {
        const s = semafori[i];
        const crossing_m = s.crossing_length_m || 10;
        
        // Find distance to the stopline of THIS signal from wherever we currently are
        const stoppingPos = s.cumulativeDistance - crossing_m;
        
        if (stoppingPos > simPos) {
          const distToStop = stoppingPos - simPos;
          const currentVelocityMs = (s.velocita_link || velocity) * 1000 / 3600;
          const timeToReach = distToStop / currentVelocityMs;

          // Moving to signal line
          simTime += timeToReach;
          simPos = stoppingPos;
          points.push({ t: simTime, x: simPos });
        }

        // Check if need to wait at the light
        const state = getTrafficLightState(s, simTime);
        if (state !== 'green') {
          const T = getCycleTime(s);
          const offset = s.inizio_verde || 0;
          const green_start = s.green_start || 0;
          
          const tCycle = ((simTime - offset) % T + T) % T;
          let timeToNextGreen = 0;
          if (tCycle < green_start) {
            timeToNextGreen = green_start - tCycle;
          } else {
            timeToNextGreen = T - tCycle + green_start;
          }
          
          // Waiting at signal (horizontal line in 2D chart)
          simTime += timeToNextGreen;
          points.push({ t: simTime, x: simPos });
        }

        // Crossing Intersection Time
        const currentVelocityMs = (s.velocita_link || velocity) * 1000 / 3600;
        const crossingVelocityMs = currentVelocityMs * 0.7; // Factor 0.7 for intersection crossing
        const crossingTime = crossing_m / crossingVelocityMs;
        
        simTime += crossingTime;
        simPos += crossing_m;
        points.push({ t: simTime, x: simPos });
      }

      // Final point (a bit after the last signal)
      const finalVelocityMs = (semafori[semafori.length - 1]?.velocita_link || velocity) * 1000 / 3600;
      points.push({ t: simTime + 60, x: simPos + (60 * finalVelocityMs) });

      return points;
    }, [semafori, velocity, firstSignalDistance, carStartOffset, getTrafficLightState]);

  // Early return after all hooks
  if (!visible) return null;



  return (
    <div className="tlgw-simulation-overlay tlgw-simulation-overlay-transparent">
      <div
        ref={modalRef}
        className="tlgw-simulation-modal"
        style={{
          transform: `translate(${position.x}px, ${position.y}px)`,
          cursor: isDragging ? 'grabbing' : 'default'
        }}
        onClick={e => e.stopPropagation()}
      >
        {/* Header - Draggable area */}
        <div
          className="tlgw-simulation-header tlgw-simulation-header-draggable"
          onMouseDown={handleDragStart}
          style={{ cursor: isDragging ? 'grabbing' : 'grab' }}
        >
          <div className="tlgw-simulation-title-section">
            <CarOutlined className="tlgw-simulation-icon" />
            <div>
              <h3 className="tlgw-simulation-title">Simulazione Onda verde su direttrice</h3>
              {viewMode === '1D' && (
                <span className="tlgw-simulation-subtitle">velocità: {velocity} km/h</span>
              )}
            </div>
          </div>

          <div className="tlgw-simulation-controls">
            {/* Speed Control */}
            {viewMode === '1D' && (
              <div className="tlgw-speed-control" style={{ marginRight: '15px', display: 'flex', gap: '5px' }}>
                <button
                  className={`tlgw-speed-btn ${speedMultiplier === 1 ? 'active' : ''}`}
                  onClick={() => setSpeedMultiplier(1)}
                >
                  1x
                </button>
                <button
                  className={`tlgw-speed-btn ${speedMultiplier === 2 ? 'active' : ''}`}
                  onClick={() => setSpeedMultiplier(2)}
                >
                  2x
                </button>
                <button
                  className={`tlgw-speed-btn ${speedMultiplier === 3 ? 'active' : ''}`}
                  onClick={() => setSpeedMultiplier(3)}
                >
                  3x
                </button>
              </div>
            )}

            {/* View Toggle */}
            <div className="tlgw-simulation-toggle">
              <button
                className={`tlgw-toggle-btn ${viewMode === '1D' ? 'active' : ''}`}
                onClick={() => setViewMode('1D')}
              >
                1D
              </button>
              <button
                className={`tlgw-toggle-btn ${viewMode === '2D' ? 'active' : ''}`}
                onClick={() => setViewMode('2D')}
              >
                2D
              </button>
            </div>

            <button className="tlgw-simulation-close" onClick={onClose}>
              <CloseOutlined />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="tlgw-simulation-content">
          {viewMode === '1D' ? (
            <div className="tlgw-simulation-1d">
              {/* Play Button */}
              <div style={{ display: "flex", gap: "10px" }}>
                <button
                  className={`tlgw-play-btn ${isPlaying ? 'playing' : ''}`}
                  onClick={handlePlayPause}
                >
                  {isPlaying ? <FaPause /> : <FaPlay />}
                </button>

                {/* Simulation Timer */}
                <div className="tlgw-simulation-timer">
                  <span className="tlgw-timer-icon">⏱</span>
                  <span className="tlgw-timer-value">{currentTime.toFixed(1)}s</span>
                  <span className="tlgw-timer-total"> / {totalDuration.toFixed(1)}s</span>
                </div>
              </div>

              {/* 1D Visualization */}
              <div className="tlgw-simulation-track">
                {/* Dashed track line (car path) */}
                <div className="tlgw-track-path" />

                {/* Traffic Lights - centered on the track */}
                {semafori.map((semaforo) => {
                  const position = maxScaleDistance > 0
                    ? ((semaforo.cumulativeDistance + carStartOffset) / (maxScaleDistance + carStartOffset)) * 100
                    : 0;
                  const state = getTrafficLightState(semaforo, currentTime);

                  return (
                    <div
                      key={semaforo.id || semaforo.index}
                      className="tlgw-traffic-light-1d"
                      style={{ left: `${position}%` }}
                    >
                      <span className="tlgw-light-label">{semaforo.semaforo || semaforo.id || semaforo.nome || `S${semaforo.index + 1}`}</span>
                      <div className={`tlgw-light-bar ${state}`} />
                      <span className="tlgw-light-distance">{semaforo.cumulativeDistance}m</span>
                    </div>
                  );
                })}

                {/* Multiple Cars - Using Precomputed CFM Trajectories */}
                {precomputedData && precomputedData.cars.map((car, carIdx) => {
                  const f = Math.floor(currentTime / precomputedData.dt);
                  if (f < 0 || f >= car.trajectory.length || currentTime < car.spawnTime) return null;

                  const carDist = car.trajectory[f];
                  const carWaiting = car.isWaiting[f] === 1;
                  const carPosPct = ((carDist + carStartOffset) / (maxScaleDistance + carStartOffset)) * 100;

                  // Don't show car if past the end
                  if (carPosPct > 105) return null;

                  return (
                    <div
                      key={`car-${carIdx}`}
                      className={`tlgw-car ${carWaiting ? 'waiting' : ''}`}
                      style={{
                        left: `${Math.min(Math.max(carPosPct, 0), 100)}%`,
                        display: 'block',
                        opacity: carIdx === 0 ? 1 : 0.7,
                      }}
                    >
                      <img src={carIcon} alt="Auto" className="tlgw-car-icon" />
                      {carWaiting && isPlaying && carIdx === 0 && <div className="tlgw-car-status">In attesa...</div>}
                    </div>
                  );
                })}
              </div>

              {/* Distance Scale - 5px gap between each mark */}
              <div className="tlgw-distance-scale">
                {scaleMarks.map((distance) => (
                  <span
                    key={distance}
                    className="tlgw-scale-mark"
                    style={{
                      left: `${((distance + carStartOffset) / (maxScaleDistance + carStartOffset)) * 100}%`
                    }}
                  >
                    {distance}m
                  </span>
                ))}
              </div>
            </div>
          ) : (
            /* 2D View - Static display with rectangles */
            <div className="tlgw-simulation-2d">
              <div className="tlgw-chart-container">
                {/* Y Axis - Distance */}
                <div className="tlgw-chart-y-axis">
                  <span className="tlgw-axis-label-vertical">Distanza (m)</span>
                  <div className="tlgw-y-axis-ticks" style={{ position: 'relative', height: '100%', display: 'block' }}>
                    {yAxisTicks.map((distance) => (
                      <span 
                        key={distance} 
                        className="tlgw-axis-tick"
                        style={{ 
                          position: 'absolute', 
                          bottom: `${((distance - minY) / yRange) * 100}%`,
                          transform: 'translateY(50%)',
                          right: '10px'
                        }}
                      >
                        {distance}m
                      </span>
                    ))}
                  </div>
                </div>

                {/* Chart Area */}
                <div className="tlgw-chart-main">
                  <div className="tlgw-chart-area" style={{ position: 'relative' }}>
                    <svg
                      width="100%"
                      height="100%"
                      viewBox={`0 0 ${chartWidth} ${chartHeight}`}
                      preserveAspectRatio="none"
                      style={{ display: 'block' }}
                    >
                      {/* Grid lines - horizontal */}
                      {yAxisTicks.map((distance, i) => (
                        <line
                          key={`h-${i}`}
                          x1="0"
                          y1={chartHeight - ((distance - minY) * yScale)}
                          x2={chartWidth}
                          y2={chartHeight - ((distance - minY) * yScale)}
                          stroke="#b0b0b0"
                          strokeWidth="1"
                        />
                      ))}

                      {/* Grid lines - vertical (time) */}
                      {Array.from({ length: Math.ceil(maxTime / 100) + 1 }, (_, i) => i * 100).map((time) => (
                        <line
                          key={`v-${time}`}
                          x1={time * xScale}
                          y1="0"
                          x2={time * xScale}
                          y2={chartHeight}
                          stroke="#b0b0b0"
                          strokeWidth="1"
                        />
                      ))}

                      {/* Bandwidth Analysis Ported from Python plot_green_band_valid */}
                      {semafori.map((signal, i) => {
                        const T = getCycleTime(signal);
                        const offset = signal.inizio_verde || 0;
                        const linkIn = i > 0 ? true : false;
                        const yCenter = chartHeight - ((dist2D[i] - minY) * yScale);
                        
                        const bars = [];
                        for (let tStart = offset; tStart < maxTime + T; tStart += T) bars.push(tStart);
                        for (let tStart = offset - T; tStart > -T; tStart -= T) bars.unshift(tStart);
                        
                        return (
                          <g key={`timeline-${signal.id || signal.index}`}>
                            {bars.map((cycleStart, bIdx) => {
                              const green_start = signal.green_start || 0;
                              
                              // Green Phase
                              const gStart = cycleStart + green_start;
                              const gEnd = gStart + (signal.durata_verde || 20);
                              
                              // Yellow Phase
                              const yStart = gEnd;
                              const yEnd = yStart + (signal.yellow_time || 3);
                              
                              // Red Phase
                              const rStart = yEnd;
                              const rEnd = cycleStart + T;
                              
                              // Effective Green
                              const [g0_eff, g1_eff] = getEffectiveGreenRelative(signal, linkIn);
                              const eff_start = cycleStart + g0_eff;
                              const eff_end = cycleStart + g1_eff;

                              return (
                                <g key={`${signal.id}-phases-${bIdx}`}>
                                  {/* Red Before Green */}
                                  {green_start > 0 && (
                                    <rect x={cycleStart * xScale} y={yCenter - 30 * yScale} width={green_start * xScale} height={60 * yScale} fill="red" opacity="0.25" />
                                  )}
                                  {/* Base Green */}
                                  <rect x={gStart * xScale} y={yCenter - 30 * yScale} width={(gEnd - gStart) * xScale} height={60 * yScale} fill="green" opacity="0.35" />
                                  {/* Yellow */}
                                  <rect x={yStart * xScale} y={yCenter - 30 * yScale} width={(yEnd - yStart) * xScale} height={60 * yScale} fill="orange" opacity="0.5" />
                                  {/* Red After Yellow */}
                                  <rect x={rStart * xScale} y={yCenter - 30 * yScale} width={(rEnd - rStart) * xScale} height={60 * yScale} fill="red" opacity="0.25" />
                                  {/* Effective Green (Lime) */}
                                  <rect x={eff_start * xScale} y={yCenter - 12 * yScale} width={(eff_end - eff_start) * xScale} height={24 * yScale} fill="lime" opacity="0.9" />
                                </g>
                              );
                            })}
                            
                            <text
                              x={10}
                              y={yCenter - (30 * yScale) - 10}
                              fontSize="20"
                              fill="#000"
                              fontWeight="bold"
                            >
                              {signal.semaforo || signal.id || signal.nome || `S${signal.index + 1}`}
                            </text>
                          </g>
                        );
                      })}

                      {/* Valid Green Wave Band Polygons */}
                      {(() => {
                        if (semafori.length === 0) return null;
                        
                        const s0 = semafori[0];
                        const offset0 = s0.inizio_verde || 0;
                        const [g0_first_rel, g1_first_rel] = getEffectiveGreenRelative(s0, false);
                        const baseCycle = getCycleTime(s0);
                        
                        let waveBands = [];
                        
                        for (let k = 0; (offset0 + k * baseCycle) < maxTime; k++) {
                          const tau_blue_start = offset0 + k * baseCycle + g0_first_rel;
                          const tau_blue_end = offset0 + k * baseCycle + g1_first_rel;
                          
                          // 1. Theoretical Blue Band Lines (optional to draw but we focus on valid green for visual clarity,
                          //    drawing transparent blue polygon first)
                          const bluePoints = [
                            ...cum_tt.map((t, idx) => `${(tau_blue_start + t) * xScale},${chartHeight - ((dist2D[idx] - minY) * yScale)}`),
                            ...cum_tt.slice().reverse().map((t, idx) => `${(tau_blue_end + t) * xScale},${chartHeight - ((dist2D[dist2D.length - 1 - idx] - minY) * yScale)}`)
                          ].join(" ");
                          
                          waveBands.push(
                            <g key={`blue-band-${k}`}>
                              <polygon points={bluePoints} fill="blue" opacity="0.12" />
                              <polyline points={cum_tt.map((t, idx) => `${(tau_blue_start + t) * xScale},${chartHeight - ((dist2D[idx] - minY) * yScale)}`).join(" ")} fill="none" stroke="blue" strokeWidth="2" opacity="0.4" />
                              <polyline points={cum_tt.map((t, idx) => `${(tau_blue_end + t) * xScale},${chartHeight - ((dist2D[idx] - minY) * yScale)}`).join(" ")} fill="none" stroke="blue" strokeWidth="2" opacity="0.4" />
                            </g>
                          );
                          
                          // 2. Compute Valid Green Intersection
                          let current_intervals = [[tau_blue_start, tau_blue_end]];
                          
                          for (let i = 0; i < semafori.length; i++) {
                            const s = semafori[i];
                            const linkIn = i > 0 ? true : false;
                            const [g0_rel, g1_rel] = getEffectiveGreenRelative(s, linkIn);
                            const off = s.inizio_verde || 0;
                            const Ti = cum_tt[i];
                            const C = getCycleTime(s);
                            
                            let new_current = [];
                            
                            for (const [tau_a, tau_b] of current_intervals) {
                              const m_min = Math.floor((tau_a + Ti - off - g1_rel) / C) - 1;
                              const m_max = Math.floor((tau_b + Ti - off - g0_rel) / C) + 1;
                              
                              for (let m = m_min; m <= m_max; m++) {
                                const a = off + m * C + g0_rel - Ti;
                                const b = off + m * C + g1_rel - Ti;
                                
                                const left = Math.max(tau_a, a);
                                const right = Math.min(tau_b, b);
                                
                                if (right > left) {
                                  new_current.push([left, right]);
                                }
                              }
                            }
                            
                            if (new_current.length === 0) {
                              current_intervals = [];
                              break;
                            }
                            current_intervals = new_current;
                          }
                          
                          // Draw Valid Green Band Polygons
                          current_intervals.forEach((interval, idx) => {
                            const [tau_valid_start, tau_valid_end] = interval;
                            
                            const greenPoints = [
                              ...cum_tt.map((t, i) => `${(tau_valid_start + t) * xScale},${chartHeight - ((dist2D[i] - minY) * yScale)}`),
                              ...cum_tt.slice().reverse().map((t, i) => `${(tau_valid_end + t) * xScale},${chartHeight - ((dist2D[dist2D.length - 1 - i] - minY) * yScale)}`)
                            ].join(" ");
                            
                            waveBands.push(
                              <g key={`green-band-${k}-${idx}`}>
                                <polygon points={greenPoints} fill="limegreen" opacity="0.35" />
                                <polyline points={cum_tt.map((t, i) => `${(tau_valid_start + t) * xScale},${chartHeight - ((dist2D[i] - minY) * yScale)}`).join(" ")} fill="none" stroke="green" strokeWidth="3" />
                                <polyline points={cum_tt.map((t, i) => `${(tau_valid_end + t) * xScale},${chartHeight - ((dist2D[i] - minY) * yScale)}`).join(" ")} fill="none" stroke="green" strokeWidth="3" />
                              </g>
                            );
                          });
                        }
                        return waveBands;
                      })()}
                    </svg>
                  </div>

                  {/* X Axis - Time */}
                  <div className="tlgw-chart-x-axis" style={{ position: 'relative', width: '100%', height: '30px' }}>
                    {Array.from({ length: Math.ceil(maxTime / 100) + 1 }, (_, i) => i * 100).map((time) => (
                      <span
                        key={time}
                        className="tlgw-axis-tick"
                        style={{ position: 'absolute', left: `${(time / maxTime) * 100}%`, transform: 'translateX(-50%)', top: '0px' }}
                      >
                        {time}s
                      </span>
                    ))}
                  </div>
                  <div className="tlgw-x-axis-label">Tempo (s)</div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Legend */}
        <div className="tlgw-simulation-legend">
          <div className="tlgw-legend-item">
            <span className="tlgw-legend-line-dashed" style={{ backgroundColor: '#A1E430', width: '24px', marginRight: '8px' }} />
            <span>Onda Verde Valida</span>
          </div>
          <div className="tlgw-legend-item">
            <span className="tlgw-legend-line-dashed" style={{ backgroundColor: 'rgba(0, 0, 255, 0.4)', width: '24px', marginRight: '8px' }} />
            <span>Banda Teorica (Blu)</span>
          </div>
          <div className="tlgw-legend-item">
            <span className="tlgw-legend-dot" style={{ backgroundColor: '#ff4d4f' }} />
            <span>Rosso</span>
          </div>
          <div className="tlgw-legend-item">
            <span className="tlgw-legend-dot" style={{ backgroundColor: '#FFD700' }} />
            <span>Giallo</span>
          </div>
          <div className="tlgw-legend-item">
            <span className="tlgw-legend-dot" style={{ backgroundColor: 'lime' }} />
            <span>Verde</span>
          </div>
        </div>
      </div>
    </div>
  );
}

export default SimulazioneModal;
